import { api, type DirectivesContext, type ApplicableDirective } from '../api';
import { callout, field, input, opt, openDialog, select, todayISO, val } from '../dialog';
import { esc, fmtHours, html } from '../ui';

/**
 * Recording compliance with a directive outside a work pack.
 *
 * The usual route is a work pack — the AD is an item, it gets signed off, the
 * aircraft is released. But a great deal of AD compliance is historic: found
 * during a records check, or done before the aircraft came under management.
 * Refusing to record that is how an AD status ends up permanently wrong, so
 * this exists and asks for the evidence instead.
 */

const METHODS = [
  ['INSPECTION', 'Inspection'],
  ['MODIFICATION', 'Modification'],
  ['REPLACEMENT', 'Replacement'],
  ['TERMINATING_ACTION', 'Terminating action'],
  ['RECORDS_CHECK', 'Records check — found in the aircraft records'],
] as const;

export function recordCompliance(
  ctx: DirectivesContext,
  preselect: ApplicableDirective | null,
  reload: () => void,
): void {
  let directiveId = preselect?.id ?? ctx.directives[0]?.id ?? '';
  let terminating = false;

  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Record directive compliance',
    sub: `${ctx.registration} — currently at ${fmtHours(ctx.tsnHours)} h`,
    primary: 'Record compliance',
    body: () => {
      const d = ctx.directives.find((x) => x.id === directiveId);
      return html`
        ${field('Directive', select('directiveId',
          ctx.directives.map((x) => opt(x.id, `${x.authority} ${x.reference}${x.revision ?? ''} — ${x.subject}`, x.id === directiveId)).join(''),
          'data-directive'))}

        ${d
          ? callout(
              d.state === 'OVERDUE' ? 'exceed' : d.state === 'DUE_SOON' ? 'caution' : 'info',
              d.state === 'OVERDUE' ? '✕' : d.state === 'DUE_SOON' ? '!' : 'i',
              html`
                ${esc(d.subject)}.
                ${d.isRecurring
                  ? `Repeats every ${d.recurringHours ? `${d.recurringHours} h` : `${d.recurringMonths} months`}.`
                  : 'One-off — no repeat interval.'}
                ${d.state ? ` Currently <b>${esc(d.state.toLowerCase().replace('_', ' '))}</b>.` : ''}
                ${d.terminatingActionAvailable
                  ? ' A terminating action is available, which ends the repeat.'
                  : ''}
              `,
            )
          : ''}

        <div class="frow">
          ${field('How compliance was achieved', select('method', METHODS.map(([v, l]) => opt(v, l)).join('')))}
          ${field('Complied on', input('compliedAt', todayISO(), 'type="date"'))}
        </div>

        ${d?.terminatingActionAvailable
          ? html`
              <label style="display:flex;gap:9px;align-items:flex-start;font-size:13.5px">
                <input type="checkbox" name="isTerminating" data-terminating ${terminating ? 'checked' : ''} style="margin-top:3px">
                <span>
                  This compliance is the <b>terminating action</b> — the directive no longer repeats on this
                  aircraft.
                </span>
              </label>
              ${terminating
                ? callout('caution', '!', html`
                    The recurring item will be removed from the due list entirely. Only tick this where the
                    directive's own terminating action has actually been embodied.
                  `)
                : ''}
            `
          : ''}

        ${field('Recorded by', select('recordedByPersonId',
          ctx.people.map((p) => opt(p.id, `${p.displayName}${p.jobTitle ? ` — ${p.jobTitle}` : ''}`)).join('')))}
        ${field('Evidence', input('evidence', '', 'placeholder="Logbook entry 2026-07-30; Form 1 ref F1-2244" maxlength="400"'),
          'Where the compliance is recorded — this is what an auditor asks for')}
      `;
    },
    onRender: (root, refresh) => {
      root.querySelector<HTMLSelectElement>('[data-directive]')?.addEventListener('change', (e) => {
        directiveId = (e.target as HTMLSelectElement).value;
        terminating = false;
        refresh();
      });
      root.querySelector<HTMLInputElement>('[data-terminating]')?.addEventListener('change', (e) => {
        terminating = (e.target as HTMLInputElement).checked;
        refresh();
      });
    },
    collect: (root) => {
      const evidence = val(root, 'evidence');
      if (!evidence) return { error: 'Record where the compliance is evidenced.' };
      const compliedAt = val(root, 'compliedAt');
      if (compliedAt > todayISO()) return { error: 'Compliance cannot be recorded in the future.' };
      return {
        method: val(root, 'method'),
        compliedAt,
        isTerminating: root.querySelector<HTMLInputElement>('[data-terminating]')?.checked ?? false,
        evidence,
        recordedByPersonId: val(root, 'recordedByPersonId'),
      };
    },
    submit: (payload) => api.directives.recordCompliance(ctx.aircraftId, directiveId, payload),
    done: (r) =>
      r.recurs
        ? `${r.reference} complied — next repeat set on ${r.registration}`
        : `${r.reference} complied — no further repeat`,
    onDone: reload,
  });
}
